import React from 'react';
import axios from 'axios';
import { Alert, Button, Collapse, Select, Space, Spin, Table, Tabs, Tag, message } from 'antd';
import {
  DownloadOutlined,
  LinkOutlined,
  LoadingOutlined,
  ReloadOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import CurrencyFormat from 'react-currency-format';
import ReactExport from '@ibrahimrahmani/react-export-excel';
import * as serverconfig from '../serverconn';
import { ReportEmailControls, submitReportWithEmail, reportSearchButtonLabel, REPORT_KEYS, initialReportEmailState } from '../reportEmailUtils';
import { companyAccountOptionLabel, filterCompanyAccountOption } from '../journalReportUtils';

var CryptoJS = require('crypto-js');

const { Panel } = Collapse;
const { Option } = Select;
const { TabPane } = Tabs;
const antIcon = <LoadingOutlined style={{ fontSize: 60 }} spin />;
const ExcelFile = ReactExport.ExcelFile;
const ExcelSheet = ReactExport.ExcelFile.ExcelSheet;
const ExcelColumn = ReactExport.ExcelFile.ExcelColumn;

var sacco = '';
var token = '';

function sortUnmatchedRows(rows) {
  return [...rows].sort((a, b) => {
    if (a.would_resolve_with_current_chart !== b.would_resolve_with_current_chart) {
      return a.would_resolve_with_current_chart ? 1 : -1;
    }
    return b.journal_count - a.journal_count;
  });
}

function rowKeyFor(record) {
  const s = record.account_string;
  return s === '' || s === null || s === undefined ? '__empty__' : String(s);
}

function parseApiList(data) {
  if (Array.isArray(data)) {
    return data;
  }
  if (data && Array.isArray(data.results)) {
    return data.results;
  }
  return [];
}

class UnmatchedJournalCompanyAccountReportView extends React.Component {
  state = {
    ...initialReportEmailState(),
    datarequested: true,
    saving: false,
    report: null,
    companyprofile: {},
    companyaccounts: [],
    linkSelections: {},
    activeTab: 'investigate',
  };

  getSaccoId = () => {
    if (!localStorage.getItem('sacco')) {
      return '';
    }
    return CryptoJS.AES.decrypt(localStorage.getItem('sacco'), 'my-secret-key@12345').toString(
      CryptoJS.enc.Utf8
    );
  };

  componentDidMount() {
    const saccoId = this.getSaccoId();
    sacco = saccoId;
    if (localStorage.getItem('token')) {
      token = localStorage.getItem('token');
    } else {
      token = '';
    }
    axios.defaults.headers = {
      'Content-Type': 'application/json',
      Authorization: `Token ${token}`,
    };

    if (!saccoId) {
      message.error('No sacco in session. Please log in again.');
      this.setState({ datarequested: false });
      return;
    }

    axios.get(`${serverconfig.backendserverurl}/api/saccos/${saccoId}/`)
      .then((res) => {
        this.setState((prev) => ({
          companyprofile: res.data,
          receiving_email: prev.receiving_email || res.data.company_email || '',
        }));
      })
      .catch(() => {});

    axios.get(`${serverconfig.backendserverurl}/api/companyaccounts/?sacco=${saccoId}`)
      .then((res) => {
        this.setState({ companyaccounts: parseApiList(res.data) });
      })
      .catch((err) => {
        console.log(err);
        message.warning('Could not load chart accounts for linking');
      });

    this.loadReport();
  }

  parseReportPayload = (raw) => {
    if (raw == null || raw === '') {
      return {};
    }
    if (typeof raw === 'object') {
      return raw;
    }
    if (typeof raw === 'string') {
      return JSON.parse(raw);
    }
    return {};
  };

  loadReportErrorMessage = (err) => {
    const data = err?.response?.data;
    if (typeof data === 'string' && data.trim().startsWith('<')) {
      return 'Server error (check login or gateway timeout). Try Refresh in a minute.';
    }
    if (data?.message) {
      return String(data.message);
    }
    if (err?.code === 'ECONNABORTED') {
      return 'Report timed out — large sacco; wait and use Refresh, or run backfill from the server.';
    }
    if (err?.message) {
      return err.message;
    }
    return 'Failed to load unmatched journal report';
  };

  applyUnmatchedReportResponse = (res) => {
    if (res.data?.message && res.data.message !== 'success') {
      message.error(String(res.data.message));
      return;
    }
    let report;
    try {
      report = this.parseReportPayload(res.data.report ?? res.data);
    } catch (parseErr) {
      console.log(parseErr, res.data);
      message.error('Invalid report data from server');
      return;
    }
    const rows = report.unmatched_by_account || [];
    const linkSelections = {};
    rows.forEach((row) => {
      const key = rowKeyFor(row);
      if (row.suggested_company_account_id) {
        linkSelections[key] = row.suggested_company_account_id;
      }
    });
    const investigateCount = rows.filter((r) => !r.would_resolve_with_current_chart).length;
    this.setState({
      report,
      linkSelections,
      activeTab: investigateCount > 0 ? 'investigate' : 'autolink',
      saving: false,
    });
    const unlinked = report.summary?.unlinked_company_account || 0;
    if (unlinked === 0) {
      message.success('All journals are linked to chart accounts');
    }
  };

  loadReport = () => {
    const saccoId = this.getSaccoId();
    if (!saccoId) {
      message.error('No sacco in session');
      return;
    }
    sacco = saccoId;
    const form_data = new FormData();
    form_data.append('sacco', saccoId);
    form_data.append('sample_size', 0);

    submitReportWithEmail({
      reportKey: REPORT_KEYS.UNMATCHED_JOURNAL_CHART,
      params: { sacco: saccoId, sample_size: 0 },
      emailReport: this.state.email_report,
      receivingEmail: this.state.receiving_email,
      endpoint: `${serverconfig.backendserverurl}/customqueries/get_unmatched_journal_company_account_report`,
      formData: form_data,
      setLoading: (v) => this.setState({ datarequested: v }),
      onSuccess: (res) => this.applyUnmatchedReportResponse(res),
    });
  };

  setLinkSelection = (rowKey, companyAccountId) => {
    this.setState((prev) => ({
      linkSelections: {
        ...prev.linkSelections,
        [rowKey]: companyAccountId,
      },
    }));
  };

  attachRow = (record) => {
    const saccoId = this.getSaccoId();
    const rowKey = rowKeyFor(record);
    const companyAccountId = this.state.linkSelections[rowKey];
    if (!companyAccountId) {
      message.warning('Choose a chart account from the list first');
      return;
    }

    const form_data = new FormData();
    form_data.append('sacco', saccoId);
    form_data.append('legacy_account', record.account_string || '');
    form_data.append('company_account_id', companyAccountId);

    this.setState({ saving: true });
    axios.post(
      `${serverconfig.backendserverurl}/customqueries/attach_unmatched_journals_to_chart_account`,
      form_data,
      { headers: { 'content-type': 'multipart/form-data' } }
    )
      .then((res) => {
        message.success(`Linked ${res.data.journals_updated} journal row(s)`);
        this.loadReport();
      })
      .catch((err) => {
        console.log(err);
        const msg =
          (err.response && err.response.data && err.response.data.message) ||
          'Failed to link journals';
        message.error(msg);
        this.setState({ saving: false });
      });
  };

  autoLinkAllResolvable = () => {
    const saccoId = this.getSaccoId();
    const form_data = new FormData();
    form_data.append('sacco', saccoId);

    this.setState({ saving: true });
    message.loading('Linking journals that match the chart…', 0);
    axios.post(
      `${serverconfig.backendserverurl}/customqueries/auto_attach_resolvable_unmatched_journals`,
      form_data,
      { headers: { 'content-type': 'multipart/form-data' } }
    )
      .then((res) => {
        message.destroy();
        message.success(
          `Linked ${res.data.matched} journal row(s). ${res.data.still_unmatched} still need manual mapping.`
        );
        this.loadReport();
      })
      .catch((err) => {
        message.destroy();
        console.log(err);
        const msg =
          (err.response && err.response.data && err.response.data.message) ||
          'Auto-link failed';
        message.error(msg);
        this.setState({ saving: false });
      });
  };

  buildColumns = () => [
    {
      title: 'Legacy account (journal.account)',
      dataIndex: 'account_string',
      key: 'account_string',
      width: 200,
      render: (text) => (
        <span style={{ wordBreak: 'break-word', fontWeight: 500 }}>
          {text || <Tag color="orange">(empty)</Tag>}
        </span>
      ),
    },
    {
      title: 'Count',
      dataIndex: 'journal_count',
      key: 'journal_count',
      width: 72,
      sorter: (a, b) => a.journal_count - b.journal_count,
      defaultSortOrder: 'descend',
    },
    {
      title: 'Status',
      key: 'status',
      width: 110,
      render: (_, record) =>
        record.would_resolve_with_current_chart ? (
          <Tag color="green">Auto OK</Tag>
        ) : (
          <Tag color="red">Manual</Tag>
        ),
    },
    {
      title: 'Attach chart account',
      key: 'attach',
      width: 320,
      render: (_, record) => {
        const rowKey = rowKeyFor(record);
        return (
          <Select
            showSearch
            allowClear
            style={{ width: '100%', minWidth: 260 }}
            placeholder="Search name or code…"
            value={this.state.linkSelections[rowKey] || undefined}
            filterOption={filterCompanyAccountOption}
            onChange={(val) => this.setLinkSelection(rowKey, val)}
            disabled={this.state.saving}
          >
            {this.state.companyaccounts.map((acc) => (
              <Option key={acc.id} value={acc.id} companyAccount={acc}>
                {companyAccountOptionLabel(acc)}
              </Option>
            ))}
          </Select>
        );
      },
    },
    {
      title: '',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<LinkOutlined />}
          loading={this.state.saving}
          onClick={() => this.attachRow(record)}
        >
          Link
        </Button>
      ),
    },
    {
      title: 'Debit',
      dataIndex: 'total_debit',
      key: 'total_debit',
      align: 'right',
      width: 110,
      render: (v) => <CurrencyFormat value={Number(v).toFixed(2)} displayType="text" thousandSeparator />,
    },
    {
      title: 'Credit',
      dataIndex: 'total_credit',
      key: 'total_credit',
      align: 'right',
      width: 110,
      render: (v) => <CurrencyFormat value={Number(v).toFixed(2)} displayType="text" thousandSeparator />,
    },
  ];

  renderTable = (rows, emptyText) => (
    <Table
      columns={this.buildColumns()}
      dataSource={rows.map((r) => ({ ...r, key: rowKeyFor(r) }))}
      pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
      size="small"
      bordered
      locale={{ emptyText }}
      expandable={{
        expandedRowRender: (record) => (
          <div style={{ maxWidth: 900 }}>
            <p>
              Parsed: name=<strong>{record.parsed_name || '—'}</strong>, code=
              <strong>{record.parsed_code || '—'}</strong>
            </p>
            {record.suggested_company_account_id ? (
              <p>
                Suggested: {record.suggested_company_account_name} | {record.suggested_company_account_code}
              </p>
            ) : null}
            <p>Sample journal lines:</p>
            <ul>
              {(record.sample_journals || []).map((s) => (
                <li key={s.pk}>
                  Jnl #{s.journal_no} (id {s.pk}) {s.date} — D {s.Debit} C {s.Credit} — {s.description}
                </li>
              ))}
            </ul>
          </div>
        ),
      }}
    />
  );

  render() {
    const report = this.state.report;
    if (this.state.datarequested && !report) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: 40 }}>
          <Spin indicator={antIcon} />
          <p style={{ marginTop: 16 }}>Loading unmatched journal report…</p>
        </div>
      );
    }

    if (!report) {
      return (
        <div style={{ padding: 16 }}>
          <h3>Unmatched journals — chart of accounts link</h3>
          <Alert
            type="error"
            showIcon
            message="Report could not be loaded"
            description="Click Refresh. If it keeps failing, check the browser Network tab for get_unmatched_journal_company_account_report (status and response)."
            action={
              <Button type="primary" icon={<ReloadOutlined />} onClick={this.loadReport}>
                Refresh
              </Button>
            }
          />
        </div>
      );
    }
    const summary = report?.summary || {};
    const allRows = sortUnmatchedRows(report?.unmatched_by_account || []);
    const manualRows = allRows.filter((r) => !r.would_resolve_with_current_chart);
    const autoRows = allRows.filter((r) => r.would_resolve_with_current_chart);

    const excelRows = allRows.map((r) => ({
      ...r,
      status: r.would_resolve_with_current_chart ? 'Auto OK' : 'Manual',
      suggested: r.suggested_company_account_id
        ? `${r.suggested_company_account_name} | ${r.suggested_company_account_code}`
        : '',
    }));

    return (
      <div style={{ padding: 16 }}>
        <h3>Unmatched journals — chart of accounts link</h3>
        <p>
          {this.state.companyprofile.sacco_name || report?.sacco_name} — map legacy journal text to a chart
          account, then click <strong>Link</strong> (or auto-link the bulk that already match).
        </p>

        <ReportEmailControls
          emailReport={this.state.email_report}
          receivingEmail={this.state.receiving_email}
          onEmailReportChange={(val) => this.setState({ email_report: val })}
          onReceivingEmailChange={(val) => this.setState({ receiving_email: val })}
          companyEmail={this.state.companyprofile.company_email}
        />

        <Space wrap style={{ marginBottom: 16 }}>
          <Button type="primary" icon={<ReloadOutlined />} onClick={this.loadReport} disabled={this.state.saving}>
            {reportSearchButtonLabel(this.state.email_report) === 'Search' ? 'Refresh' : reportSearchButtonLabel(this.state.email_report)}
          </Button>
          <Button
            type="primary"
            icon={<ThunderboltOutlined />}
            onClick={this.autoLinkAllResolvable}
            loading={this.state.saving}
            disabled={!summary.journals_resolvable_with_current_chart}
          >
            Auto-link all matching ({summary.journals_resolvable_with_current_chart || 0} rows)
          </Button>
          {allRows.length > 0 ? (
            <ExcelFile
              filename={`unmatched-journal-chart-link-${sacco}`}
              element={<Button icon={<DownloadOutlined />}>Download Excel</Button>}
            >
              <ExcelSheet data={excelRows} name="Unmatched">
                <ExcelColumn label="Legacy account" value="account_string" />
                <ExcelColumn label="Parsed name" value="parsed_name" />
                <ExcelColumn label="Parsed code" value="parsed_code" />
                <ExcelColumn label="Journal count" value="journal_count" />
                <ExcelColumn label="Status" value="status" />
                <ExcelColumn label="Suggested chart" value="suggested" />
                <ExcelColumn label="Total debit" value="total_debit" />
                <ExcelColumn label="Total credit" value="total_credit" />
              </ExcelSheet>
            </ExcelFile>
          ) : null}
        </Space>

        <Collapse defaultActiveKey={['summary']} style={{ marginBottom: 16 }}>
          <Panel header="Summary" key="summary">
            <p>Total journals: {summary.total_journals}</p>
            <p>Linked: {summary.linked_to_company_account}</p>
            <p>
              <strong>Unlinked: {summary.unlinked_company_account}</strong> ({summary.unlinked_percent}%)
            </p>
            <p>
              <Tag color="red">Manual</Tag> {manualRows.length} account string(s),{' '}
              {summary.journals_needing_chart_or_data_fix ?? '—'} journal row(s)
            </p>
            <p>
              <Tag color="green">Auto OK</Tag> {autoRows.length} account string(s),{' '}
              {summary.journals_resolvable_with_current_chart} journal row(s) — use Auto-link above
            </p>
            <Alert
              type="info"
              showIcon
              style={{ marginTop: 8 }}
              message="How to use"
              description="Pick a chart account in each row and click Link. Rows marked Auto OK already match the chart — use Auto-link all matching for those in one step."
            />
          </Panel>
        </Collapse>

        {allRows.length === 0 ? (
          <Alert type="success" showIcon message="No unlinked journals for this sacco." />
        ) : (
          <Tabs activeKey={this.state.activeTab} onChange={(key) => this.setState({ activeTab: key })}>
            <TabPane
              tab={`Needs manual mapping (${manualRows.length})`}
              key="investigate"
            >
              {manualRows.length === 0 ? (
                <Alert type="success" showIcon message="Nothing left that needs manual mapping." />
              ) : (
                this.renderTable(manualRows, 'No manual rows')
              )}
            </TabPane>
            <TabPane
              tab={`Ready to auto-link (${autoRows.length})`}
              key="autolink"
            >
              {autoRows.length === 0 ? (
                <Alert type="info" showIcon message="No auto-match rows — use manual tab or run backfill from shell." />
              ) : (
                <>
                  <Alert
                    type="success"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message={`${summary.journals_resolvable_with_current_chart} journal rows already match the chart`}
                    description="Use the button above Auto-link all matching, or link per row below (suggested account is pre-selected)."
                  />
                  {this.renderTable(autoRows, 'No auto-link rows')}
                </>
              )}
            </TabPane>
            <TabPane tab={`All (${allRows.length})`} key="all">
              {this.renderTable(allRows, 'No rows')}
            </TabPane>
          </Tabs>
        )}
      </div>
    );
  }
}

export default UnmatchedJournalCompanyAccountReportView;
